import { FC, useEffect, useMemo } from 'react'
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react'
import { useSearchParams } from 'react-router-dom'
import Page from '../../components/Page'
import PrescriberProfilePage from '../PrescriberProfilePage/PrescriberProfilePage'
import OrganizationProfilePage from '../OrganizationProfilePage'
import { SubscriptionInfo } from '../Billing/SubscriptionInfo'
import { RootState } from '../../app/store'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import { BillingClient } from '../Billing/BillingSlice'
import { OrganizationAdminProfilePage } from '../../features/organization-admin/OrganizationAdminProfilePage'
import { UsersPage } from '../../features/users/UsersPage'
import UnauthorizedPage from '../../components/UnauthorizedPage'
import { Permission } from '../../types/permission'
import { IntegrationsPage } from '../../features/integration/IntegrationsPage'
import { useFeatureToggle } from '../../common/utils/feature-toggle/useFeatureToggle'
import { PlanCode } from '../../types/subscription'

interface TabConfig {
  id: string
  label: string
  component: React.ReactNode
  isVisible: boolean
}

const SettingsPage: FC<unknown> = () => {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const authInfo = useAppSelector((state: RootState) => state.authentication)
  const loginFinished = authInfo.loginFinished
  const permissions = authInfo?.permissions ?? []
  const organizationId = useAppSelector((state: RootState) => state.organization?.data?.id)
  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const isPrescriberActive = useAppSelector((state) => state.prescriber.prescriber?.active)
  const organizationAdmin = useAppSelector((state) => state.organizationAdmin.profile)
  const isAdminActive = organizationAdmin?.active
  const subscription = useAppSelector((state: RootState) => state.billing?.subscription.data)
  const organizationPlanCode = useAppSelector(
    (state: RootState) => state.organization?.entitlements?.planCode
  )

  useEffect(() => {
    if (organizationId) {
      dispatch(BillingClient.getSubscription({ organizationId }))
    }
  }, [organizationId, dispatch])

  const isAbleToManageBilling =
    subscription?.stripeSubscriptionId &&
    organizationPlanCode !== PlanCode.UNLIMITED_ONE_YEAR &&
    organizationAdmin?.permissions?.includes(Permission.BILLING_MANAGEMENT)

  const { isFeatureEnabled: isIntegrationsFeatureOn } = useFeatureToggle('INTEGRATIONS')

  const tabConfigs: TabConfig[] = useMemo(
    () => [
      {
        id: 'prescriber',
        label: 'Prescriber Profile',
        component: <PrescriberProfilePage />,
        isVisible: loginFinished && !!isPrescriberActive,
      },
      {
        id: 'admin',
        label: 'Admin Profile',
        component: <OrganizationAdminProfilePage />,
        isVisible: loginFinished && !!isAdminActive,
      },
      {
        id: 'users',
        label: 'Users',
        component: <UsersPage />,
        isVisible: loginFinished && !!organizationSettings?.enableUsersBoard,
      },
      {
        id: 'organization',
        label: 'Organisation',
        component: <OrganizationProfilePage />,
        isVisible: loginFinished && !!organizationSettings?.enableUpdateOrganization,
      },
      {
        id: 'subscription',
        label: 'Subscription',
        component: <SubscriptionInfo subscription={subscription} />,
        isVisible: loginFinished && !!isAbleToManageBilling,
      },
      {
        id: 'integrations',
        label: 'Integrations',
        component: <IntegrationsPage />,
        isVisible:
          loginFinished &&
          !!organizationSettings?.enableAutoBuildIntegration &&
          isIntegrationsFeatureOn,
      },
    ],
    [
      loginFinished,
      isPrescriberActive,
      isAdminActive,
      organizationSettings?.enableUsersBoard,
      organizationSettings?.enableUpdateOrganization,
      organizationSettings?.enableAutoBuildIntegration,
      isAbleToManageBilling,
      subscription,
    ]
  )

  const visibleTabs = useMemo(() => tabConfigs.filter((tab) => tab.isVisible), [tabConfigs])

  const currentTab = searchParams.get('tab')
  const currentTabIndex = visibleTabs.findIndex((tab) => tab.id === currentTab)

  const handleTabChange = (index: number) => {
    const selectedTab = visibleTabs[index]
    if (selectedTab) {
      setSearchParams({ tab: selectedTab.id })
    }
  }

  useEffect(() => {
    if (currentTabIndex === -1 && visibleTabs.length > 0) {
      const requestedTab = searchParams.get('tab')

      const handleTabWithLoadingDependencies = (tabId: string) => {
        if (tabId === 'subscription') {
          if (!organizationAdmin && loginFinished) {
            return true
          }

          if (!organizationId && loginFinished) {
            return true
          }

          const hasBillingPermission = organizationAdmin?.permissions?.includes(
            Permission.BILLING_MANAGEMENT
          )

          if (hasBillingPermission && organizationId && !subscription) {
            return true
          }

          if (hasBillingPermission && subscription?.stripeSubscriptionId) {
            return true
          }
        }

        if (['users', 'organization', 'integrations'].includes(tabId)) {
          if (!organizationId && loginFinished) {
            return true
          }

          if (!organizationSettings && loginFinished && organizationId) {
            return true
          }

          const tabShouldBeVisible = {
            users: organizationSettings?.enableUsersBoard,
            organization: organizationSettings?.enableUpdateOrganization,
            integrations: organizationSettings?.enableAutoBuildIntegration,
          }[tabId]

          if (tabShouldBeVisible) {
            return true
          }
        }

        return false
      }

      if (requestedTab && handleTabWithLoadingDependencies(requestedTab)) {
        return
      }

      const defaultTab =
        visibleTabs.find((tab) => ['prescriber', 'admin'].includes(tab.id)) || visibleTabs[0]

      if (defaultTab) {
        setSearchParams({ tab: defaultTab.id })
      }
    }
  }, [
    currentTabIndex,
    visibleTabs,
    setSearchParams,
    searchParams,
    subscription,
    organizationId,
    organizationAdmin,
    organizationSettings,
    loginFinished,
    isAbleToManageBilling,
  ])

  if (permissions.length === 0) {
    return <UnauthorizedPage />
  }

  const activeTabIndex = Math.max(0, currentTabIndex)

  return (
    <Page>
      <Tabs size="lg" index={activeTabIndex} onChange={handleTabChange}>
        <TabList>
          {visibleTabs.map((tab) => (
            <Tab key={tab.id} fontWeight="bold">
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {visibleTabs.map((tab) => (
            <TabPanel key={tab.id}>{tab.component}</TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Page>
  )
}

export default SettingsPage
